// ~/components/GalleryTrigger.js
import React, { useMemo, useState } from "react";
import { PhotographIcon } from "@heroicons/react/outline";
import ToursLightbox from "~/components/ToursLightbox";
import Image from "~/components/image";

export default function GalleryTrigger({ tour, eager = false }) {
	// Limit total images shown in the lightbox (featured + slider) to 5
	const MAX_IMAGES = 5;

	// Helper: extract a GatsbyImageData (GID) from any supported node shape
	const getGID = (node) => {
		if (!node) return null;
		// If already a GID object
		if (node.images && (node.images.fallback || node.images.sources)) return node;
		// If wrapped like slider nodes
		const cs = node?.localFile?.childImageSharp;
		return cs?.gatsbyImageData || cs?.hero || cs?.tile || null;
	};

	// 1) Slider images (keep only ones with a usable GID)
	const slides = useMemo(() => {
		const raw = tour?.slider ?? [];
		return raw.filter((s) => !!getGID(s));
	}, [tour]);

	const hasSlider = slides.length > 0;

	// 2) Featured (try to coerce to a GID for lightbox)
	const featuredGID = useMemo(() => getGID(tour?.featuredImage), [tour]);

	// 3) Build lightbox array: featured first, then slider (de-dupe) → cap to 4 total
	const images = useMemo(() => {
		if (!hasSlider) return []; // keep original behavior: no lightbox when no slider
		const out = [];
		const seen = new Set();

		const keyFromGID = (gid) =>
			gid?.images?.fallback?.src || gid?.images?.sources?.[0]?.srcSet || "";

		if (featuredGID) {
			const k = keyFromGID(featuredGID);
			if (k) seen.add(k);
			out.push({
				id: `featured-${tour?.id ?? "x"}`,
				alternativeText: tour?.title || "Tour image",
				localFile: { childImageSharp: { gatsbyImageData: featuredGID } },
			});
		}

		for (const s of slides) {
			if (out.length >= MAX_IMAGES) break; // stop once we have 4 total
			const gid = getGID(s);
			const k = keyFromGID(gid);
			if (!k || seen.has(k)) continue;
			seen.add(k);
			out.push(s);
		}

		// Safety cap (in case featured duplicates were skipped and later pushes exceeded)
		return out.slice(0, MAX_IMAGES);
	}, [featuredGID, hasSlider, slides, tour?.id, tour?.title]);

	// 4) Thumbnail should prefer featured; fallback to first slide; else nothing
	const thumbForImage = useMemo(() => {
		// Prefer featured if available; pass through exactly what your <Image /> expects
		if (tour?.featuredImage) return tour.featuredImage;
		// Fallback to first slide's GID wrapped for <Image />
		const firstSlideGID = getGID(slides[0]);
		return firstSlideGID || null;
	}, [tour, slides]);

	const [open, setOpen] = useState(false);

	if (!thumbForImage) return null;

	// Clickable only when a slider exists (keeps your original UX)
	const Wrapper = hasSlider ? "button" : "div";

	return (
		<>
			<Wrapper
				type={hasSlider ? "button" : undefined}
				onClick={hasSlider ? () => setOpen(true) : undefined}
				className="relative w-full overflow-hidden rounded-t-md border-b border-gray-200 group"
				aria-label={
					hasSlider ? `Open photo gallery for ${tour?.title ?? "tour"}` : undefined
				}
			>
				{/* Use your Image wrapper so the thumb always renders, regardless of shape */}
				<Image
					image={thumbForImage}
					alt={tour?.title || "Tour image"}
					className="transform transition-transform duration-300 group-hover:scale-105"
					loading={eager ? "eager" : "lazy"}
				/>

				{hasSlider && (
					<span className="absolute bottom-2 right-2 inline-flex items-center justify-center rounded-full bg-black/60 text-white p-1.5 shadow-md">
						<PhotographIcon className="w-5 h-5" aria-hidden="true" />
					</span>
				)}
			</Wrapper>

			{/* Lightbox: featured first (if any), then up to 3 more → total 4 */}
			{hasSlider && open && (
				<ToursLightbox
					images={images}
					startIndex={0}
					onClose={() => setOpen(false)}
				/>
			)}
		</>
	);
}
